import { PropsWithChildren, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { currencyProviderApi } from 'app/modules/CurrencyProvider/currencyProviderApi';
import { LinearLoader } from 'app/theme/LinearLoader';
import { AvailableCurrency, getCurrency } from 'app/translations/timezones';

type CurrencyProviderProps = PropsWithChildren<{}>;

interface UseCurrencyResult {
  currency: AvailableCurrency | undefined;
  isLoading: boolean;
  setCurrency: (currency: AvailableCurrency) => void;
}

const useFetchCurrency = (): UseCurrencyResult => {
  const queryClient = useQueryClient();
  const setCurrency = useCallback(
    (newCurrency: AvailableCurrency) => {
      currencyProviderApi.persistSelectedCurrency(newCurrency);
      queryClient.setQueryData([currencyProviderApi.queryKey], newCurrency);
    },
    [queryClient]
  );

  const { data: currency, isLoading } = useQuery([currencyProviderApi.queryKey], currencyProviderApi.getCurrency, {
    cacheTime: Number.POSITIVE_INFINITY,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    initialData: currencyProviderApi.getInitialValue,
    onSuccess: (newCurrency) => currencyProviderApi.persistSelectedCurrency(newCurrency),
    onError: () => {
      queryClient.setQueryData([currencyProviderApi.queryKey], getCurrency());
    },
  });

  return { currency, isLoading, setCurrency };
};

export const useCurrency = (): { currency: AvailableCurrency } & Pick<UseCurrencyResult, 'setCurrency'> => {
  const { currency, setCurrency } = useFetchCurrency();

  if (!currency) {
    throw new Error(
      `useCurrency hook can be used only when the currency data has been fetched. Please make sure, that you are using useFetchCurrency above, or you have the ${currencyProviderApi.queryKey} key in your react-query cache`
    );
  }

  return { currency, setCurrency };
};

export const CurrencyProvider = ({ children }: CurrencyProviderProps): JSX.Element => {
  const { currency, isLoading } = useFetchCurrency();

  if (!currency) {
    return <LinearLoader $isLoading={isLoading} />;
  }

  return <>{children}</>;
};
